import React from "react";

const PrivacyPolicy = () => (
  <div className="legal-page-container">
    <h1>Privacy Policy</h1>
    <div className="legal-content">
      <h2>Introduction</h2>
      <p>
        Welcome to Contest Hub, your companion for tracking competitive coding
        contests. This Privacy Policy explains how we collect, use, disclose,
        and safeguard your information when you use our mobile application. We
        are committed to protecting your privacy while providing you with the
        best contest tracking experience.
      </p>
      <p>
        Please read this privacy policy carefully. If you do not agree with the
        terms of this privacy policy, please do not access the application.
      </p>
      <h2>Information We Collect</h2>
      <p>
        Contest Hub is designed with privacy in mind. We collect minimal
        information necessary to provide you with the best experience:
      </p>
      <h3>Personal Information You Provide</h3>
      <ul>
        <li>
          Google Account Information: When you sign in with Google, we collect
          your name, email address, and profile picture to personalize your
          experience and sync your data across devices.
        </li>
        <li>
          Bookmark Data: Contests you choose to bookmark are stored to help you
          track contests that matter to you.
        </li>
        <li>
          Notification Preferences: Your settings for contest reminders and
          notification timing.
        </li>
        <li>
          Streak Information: Your contest participation streak data for
          tracking your consistency and progress.
        </li>
      </ul>
      <h3>Automatically Collected Information</h3>
      <ul>
        <li>
          Firebase Analytics Data: We use Firebase Analytics to understand how
          users interact with our app. This includes anonymized data about app
          usage patterns, feature engagement, and screen views.
        </li>
        <li>
          Device Information: Device type, operating system version, and unique
          device identifiers to optimize app performance and provide technical
          support.
        </li>
        <li>
          Usage Data: Information about which contests you view, bookmark, and
          interact with to improve our recommendations and user experience.
        </li>
        <li>
          Log Data: Technical information about app crashes, errors, and
          performance metrics to help us maintain and improve the service.
        </li>
      </ul>
      <h2>How We Use Your Information</h2>
      <ul>
        <li>
          Contest Aggregation: Display upcoming competitive coding contests from
          various platforms in one unified view
        </li>
        <li>
          Personalized Reminders: Send you timely notifications about upcoming
          contests based on your preferences and bookmarked contests
        </li>
        <li>
          Bookmark Management: Allow you to save, organize, and manage contests
          of interest
        </li>
        <li>
          Streak Tracking: Track and visualize your contest participation
          streaks to help you maintain consistency
        </li>
        <li>
          Account Synchronization: Sync your bookmarks, preferences, and streak
          data across multiple devices using your Google account
        </li>
        <li>
          App Improvement: Analyze usage patterns through Firebase Analytics to
          enhance features and user experience
        </li>
        <li>
          Technical Support: Troubleshoot issues, fix bugs, and maintain app
          stability
        </li>
        <li>Legal Compliance: Comply with applicable laws and regulations</li>
      </ul>
      <h2>Our Privacy Commitment</h2>
      <p>We will NEVER:</p>
      <ul>
        <li>Sell your personal information to third parties</li>
        <li>Share your data with advertisers</li>
        <li>Send spam or promotional notifications</li>
        <li>Use your data for purposes other than improving Contest Hub</li>
      </ul>
      <h2>Third-Party Services</h2>
      <h3>Google Sign-In</h3>
      <p>
        We use Google Sign-In to authenticate users and enable data
        synchronization across devices. When you sign in with Google, we receive
        your basic profile information (name, email, profile picture) as
        permitted by Google. This information is governed by Google's Privacy
        Policy, which you can review at{" "}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://policies.google.com/privacy
        </a>
      </p>
      <h3>Firebase Analytics</h3>
      <p>
        We use Firebase Analytics to collect anonymized usage statistics and app
        performance data. Firebase Analytics helps us understand:
      </p>
      <ul>
        <li>Which features are most popular</li>
        <li>How users navigate through the app</li>
        <li>Technical performance metrics</li>
        <li>Crash reports and error logs</li>
      </ul>
      <p>
        Firebase Analytics data is anonymized and aggregated. You can learn more
        about how Firebase handles data at{" "}
        <a
          href="https://firebase.google.com/support/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://firebase.google.com/support/privacy
        </a>
      </p>
      <h3>Contest Platforms</h3>
      <p>
        Contest Hub aggregates contest information from various competitive
        coding platforms (such as Codeforces, LeetCode, CodeChef, AtCoder,
        etc.). We do not share your personal information with these platforms.
        When you click on contest links within the app, you will be directed to
        the respective platform's website, which is governed by their own
        privacy policies.
      </p>
      <h2>Data Storage and Security</h2>
      <ul>
        <li>
          Secure Storage: Your data is stored securely using Firebase's cloud
          infrastructure with encryption at rest and in transit
        </li>
        <li>
          Google Authentication: We use Google's secure OAuth 2.0 protocol for
          authentication
        </li>
        <li>
          Limited Access: Only authorized personnel have access to user data,
          and only when necessary for maintenance and support
        </li>
        <li>
          Regular Security Updates: We regularly update our security measures to
          protect against new threats
        </li>
        <li>
          Data Encryption: All data transmitted between your device and our
          servers is encrypted using HTTPS/TLS protocols
        </li>
      </ul>
      <p>
        While we strive to protect your information, no method of transmission
        over the internet or electronic storage is 100% secure. We cannot
        guarantee absolute security but are committed to protecting your data to
        the best of our ability.
      </p>
      <h2>Notifications and Reminders</h2>
      <ul>
        <li>
          Gentle Reminders: We send notifications only for contests you've
          bookmarked or shown interest in
        </li>
        <li>
          No Spam: We never send promotional, marketing, or advertisement
          notifications
        </li>
        <li>
          Full Control: You can customize notification timing, frequency, and
          content in the app settings
        </li>
        <li>
          Easy Opt-Out: You can disable notifications entirely through your
          device settings or app preferences
        </li>
        <li>
          Timely Alerts: Notifications are sent at optimal times before contests
          start to help you prepare
        </li>
      </ul>
      <h2>Your Privacy Rights</h2>
      <ul>
        <li>
          Access: Request access to the personal information we have about you
        </li>
        <li>Correction: Request correction of inaccurate or incomplete data</li>
        <li>
          Deletion: Request deletion of your account and all associated data
        </li>
        <li>
          Data Portability: Export your bookmarks, streak data, and preferences
        </li>
        <li>
          Opt-Out: Disable analytics tracking or data collection (though this
          may limit app functionality)
        </li>
        <li>
          Revoke Consent: Withdraw your consent for data processing at any time
        </li>
      </ul>
      <p>
        To exercise any of these rights, please contact us at{" "}
        <a href="mailto:champ96klabs@gmail.com">champ96klabs@gmail.com</a>
      </p>
      <h2>Data Retention</h2>
      <ul>
        <li>
          Active Accounts: Your data is retained while your account is active
        </li>
        <li>
          Account Deletion: When you delete your account, all personal data is
          permanently deleted within 30 days
        </li>
        <li>
          Analytics Data: Anonymized analytics data may be retained longer for
          statistical purposes
        </li>
        <li>
          Legal Requirements: Some data may be retained if required by law or
          for legitimate business purposes
        </li>
      </ul>
      <p>
        You can delete your account and all associated data at any time through
        the app settings or by uninstalling the app.
      </p>
      <h2>Children's Privacy</h2>
      <p>
        Contest Hub is intended for users who are at least 13 years of age. We
        do not knowingly collect personal information from children under 13. If
        you are a parent or guardian and believe your child has provided
        personal information to us, please contact us immediately at
        champ96klabs@gmail.com, and we will take steps to remove such
        information from our systems.
      </p>
      <h2>International Users</h2>
      <p>
        Contest Hub is available globally. If you are accessing the app from
        outside your country of residence, please be aware that your information
        may be transferred to, stored, and processed in countries where our
        servers are located (primarily through Firebase's global
        infrastructure).
      </p>
      <p>
        By using Contest Hub, you consent to the transfer of your information to
        countries that may have different data protection laws than your country
        of residence. We ensure that all data transfers comply with applicable
        data protection regulations.
      </p>
      <h2>Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time to reflect changes
        in our practices, technology, legal requirements, or other factors. When
        we make changes, we will:
      </p>
      <ul>
        <li>Update the "Last Updated" date at the top of this policy</li>
        <li>Notify you through the app or via email for significant changes</li>
        <li>Post the updated policy on our website and within the app</li>
      </ul>
      <p>
        We encourage you to review this Privacy Policy periodically to stay
        informed about how we are protecting your information. Your continued
        use of Contest Hub after changes are posted constitutes your acceptance
        of the updated Privacy Policy.
      </p>
      <h2>Your Consent</h2>
      <ul>
        <li>
          The collection and use of information as described in this Privacy
          Policy
        </li>
        <li>
          Our use of Google Sign-In and Firebase Analytics as outlined above
        </li>
        <li>
          The storage of your data on Firebase's secure cloud infrastructure
        </li>
        <li>
          Receiving contest reminder notifications based on your preferences
        </li>
      </ul>
      <h2>Contact Us</h2>
      <p>
        If you have any questions, concerns, or requests regarding this Privacy
        Policy or our data practices, please don't hesitate to contact us:
      </p>
      <ul>
        <li>App Name: Contest Hub</li>
        <li>
          Email:{" "}
          <a href="mailto:champ96klabs@gmail.com">champ96klabs@gmail.com</a>
        </li>
        <li>
          Website:{" "}
          <a
            href="https://contesthub.labs.champ96k.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://contesthub.labs.champ96k.com
          </a>
        </li>
      </ul>
      <p>
        We typically respond to inquiries within 48 hours. For account deletion
        requests or urgent privacy concerns, please clearly indicate this in
        your email subject line.
      </p>
    </div>
  </div>
);

export default PrivacyPolicy;
